import React from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../data-table";
import PriorityBadge from "../project-management/PriorityBadge";
import ProjectProgressBar from "../project-management/ProjectProgressBar";
import DashboardPanel from "./DashboardPanel";

const REASON_STYLES = {
  overdue: "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30",
  dueSoon: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-500/30",
  behind: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30",
};

const PRIORITY_ORDER = { Critical: 4, High: 3, Medium: 2, Moderate: 2, Low: 1 };

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit", year: "numeric" }).format(date);
};

const TABLE_LABELS = { empty: "All active projects are on track." };

const columns = [
  {
    key: "projectName",
    label: "Project",
    sortable: true,
    searchable: true,
    render: (value, project) => (
      <div className="min-w-0">
        <div className="truncate font-medium text-app-primary">{value}</div>
        <div className="truncate text-xs text-app-muted">{project.projectType}</div>
      </div>
    ),
  },
  {
    key: "reason",
    label: "Issue",
    sortable: true,
    sortValue: (project) => project.reason.rank,
    render: (reason) => (
      <span
        className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${REASON_STYLES[reason.kind]}`}
      >
        {reason.label}
      </span>
    ),
  },
  {
    key: "percentage",
    label: "Progress",
    sortable: true,
    width: "170px",
    render: (percentage, project) => (
      <div>
        <div className="mb-1 flex items-center justify-between text-[11px] font-semibold text-app-secondary">
          <span>{`${project.totalRecruitsCount || 0}/${project.totalTasks || 0}`}</span>
          <span>{`${percentage}%`}</span>
        </div>
        <ProjectProgressBar percentage={percentage} />
      </div>
    ),
  },
  // {
  //   key: "priority",
  //   label: "Priority",
  //   sortable: true,
  //   sortValue: (project) => PRIORITY_ORDER[project.priority] ?? null,
  //   render: (value) => (value ? <PriorityBadge priority={value} /> : <span className="text-app-muted">—</span>),
  // },
  { key: "dueDate", label: "Due", sortable: true, render: formatDate },
  {
    key: "assignedUsers",
    label: "Assigned",
    searchable: true,
    render: (users = []) =>
      users.length === 0 ? (
        <span className="text-app-muted">—</span>
      ) : (
        <div className="max-w-[180px] text-xs" title={users.join(", ")}>
          <div className="truncate font-medium text-app-secondary">{users[0]}</div>
          {users.length > 1 && <div className="text-app-muted">+{users.length - 1} more</div>}
        </div>
      ),
  },
];

/**
 * Active projects that are overdue, due within a week, or behind schedule.
 * Rows open the project board with the same navigation state the Project
 * Management page passes.
 */
function ProjectsAttentionTable({ projects = [], loading = false, error = false }) {
  const navigate = useNavigate();

  return (
    <DashboardPanel
      title="Projects Needing Attention"
      description="Active projects that are overdue, due within 7 days, or behind schedule."
      action={{ label: "All projects", to: "/admin/project-management" }}
    >
      <DataTable
        ariaLabel="Projects needing attention"
        columns={columns}
        data={projects}
        loading={loading}
        error={error ? "Couldn't load projects." : undefined}
        pagination
        defaultPageSize={5}
        pageSizeOptions={[5, 10, 20]}
        onRowClick={(project) =>
          navigate(`/admin/project-management/${project.backendId || project.projectId}`, {
            state: { project },
          })
        }
        labels={TABLE_LABELS}
      />
    </DashboardPanel>
  );
}

export default ProjectsAttentionTable;
